/**
 * ProjectGrid — renders project thumbnails + skeleton + empty states.
 *
 * Three render modes:
 *   - loading=true → skeleton placeholder cards
 *   - projects.length===0 → "no projects yet" CTA
 *   - filteredProjects.length===0 (but projects.length>0) → empty filter state
 *   - default → grid of cards
 */
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FolderOpen, Presentation, Music, Edit, Play, Trash2, Clock, Plus, BookOpen, Sparkles,
} from "lucide-react";

function ProjectCard({ project, t, onDelete }) {
  const navigate = useNavigate();
  return (
    <Card
      className="group cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1"
      onClick={() => navigate(`/editor/${project.id}`)}
      data-testid={`project-card-${project.id}`}
    >
      <CardContent className="p-0">
        <div
          className="aspect-video relative overflow-hidden rounded-t-lg"
          style={{
            backgroundColor: project.slides?.[0]?.background_color || "#1a1a2e",
            backgroundImage: project.slides?.[0]?.background_image
              ? `url(${project.slides[0].background_image})`
              : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center p-4">
            <p className="text-white text-sm font-lyrics text-center line-clamp-3 drop-shadow-lg">
              {project.slides?.[0]?.content || t("empty_slide")}
            </p>
          </div>
          <div className="absolute top-2 left-2 flex gap-1">
            {project.is_song && (
              <Badge className="bg-purple-500 text-white text-xs">
                <Music className="w-3 h-3 mr-1" />
                {t("song")}
              </Badge>
            )}
          </div>
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button size="sm" className="gap-1">
              <Edit className="w-3 h-3" />
              Edit
            </Button>
            <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); navigate(`/present/${project.id}`); }}>
              <Play className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="destructive" onClick={(e) => onDelete(project.id, e)}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
        <div className="p-3">
          <h4 className="font-medium truncate">{project.name}</h4>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {project.slides?.length || 0} slides
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <Card key={i} className="animate-pulse">
          <CardContent className="p-4">
            <div className="aspect-video bg-slate-200 dark:bg-slate-700 rounded mb-3" />
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-2" />
            <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function ProjectGrid({ t, loading, projects, filteredProjects, onDelete, onCreate, onClearFilter, onFromVerse }) {
  if (loading) return <SkeletonGrid />;

  if (filteredProjects.length === 0 && projects.length > 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-12 text-center">
          <FolderOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-xl font-semibold mb-2">{t("no_projects_in_filter") || "No projects in this category yet"}</h3>
          <Button variant="outline" onClick={onClearFilter} data-testid="clear-filter-btn">
            {t("show_all") || "Show all"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (projects.length === 0) {
    return (
      <Card className="border-dashed border-amber-500/40 bg-amber-500/5">
        <CardContent className="p-12 text-center">
          <div className="inline-flex w-16 h-16 rounded-full bg-amber-500/15 items-center justify-center mb-4">
            <BookOpen className="w-8 h-8 text-amber-500" />
          </div>
          <h3 className="text-xl font-semibold mb-2">{t("empty_dashboard_title") || "Try it: paste a Bible verse"}</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            {t("empty_dashboard_desc") || "Type 'Romans 8:28' or 'Psalm 23' and SlideCon will draft an outline and build a slide deck for you — no setup needed."}
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              onClick={onFromVerse}
              className="gap-2 bg-amber-600 hover:bg-amber-700"
              data-testid="empty-from-verse-btn"
            >
              <Sparkles className="w-4 h-4" />
              {t("empty_from_verse_cta") || "Generate from a verse"}
            </Button>
            <Button onClick={onCreate} variant="outline" className="gap-2" data-testid="empty-create-btn">
              <Plus className="w-4 h-4" />
              {t("create_project")}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {filteredProjects.map((project, idx) => (
        <motion.div
          key={project.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.03 }}
        >
          <ProjectCard project={project} t={t} onDelete={onDelete} />
        </motion.div>
      ))}
    </div>
  );
}
